import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  FiTrendingUp,
  FiRefreshCw,
  FiActivity,
  FiArrowUpRight,
  FiArrowDownRight,
  FiCalendar,
  FiChevronDown,
  FiAlertCircle,
} from "react-icons/fi";
import { FaDragon, FaPlane } from "react-icons/fa";
import { EarningsOverview } from "../../api/endpoints";

// ============ GAME CONFIG ============
const GAME_CONFIG = {
  DRAGON_TIGER: {
    name: "Dragon Tiger",
    icon: <FaDragon />,
    accentColor: "#4F46E5",
    accentBg: "#EEF2FF",
  },
  AVIATOR: {
    name: "Aviator",
    icon: <FaPlane />,
    accentColor: "#0891B2",
    accentBg: "#ECFEFF",
  },
};

// ✅ Backend filter keys
const FILTER_OPTIONS = [
  { key: "today", label: "Today", short: "Today" },
  { key: "last7", label: "Last 7 Days", short: "7D" },
  { key: "last15", label: "Last 15 Days", short: "15D" },
  { key: "lastMonth", label: "Last 1 Month", short: "1M" },
];

// ✅ Auto-refresh interval (in milliseconds)
const AUTO_REFRESH_INTERVAL = 30000; // 30 seconds

const AdminEarning = () => {
  const [filter, setFilter] = useState("today");
  const [filterOpen, setFilterOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false); // ✅ filter change loading
  const [error, setError] = useState(null);
  const [apiData, setApiData] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [animatedTotals, setAnimatedTotals] = useState({
    totalProfit: 0,
    totalLoss: 0,
    netResult: 0,
  });

  // ✅ Use a ref to always have the latest filter inside setInterval
  const filterRef = useRef(filter);
  useEffect(() => {
    filterRef.current = filter;
  }, [filter]);

  // ============ FORMAT CURRENCY ============
  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null || isNaN(amount)) return "₹ 0";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Math.abs(amount));
  };

  // ============ FORMAT TIME ============
  const formatTime = (date) => {
    if (!date) return "";
    return new Date(date).toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  // ============ FETCH ============
  // ✅ `silent` = true means don't show loading spinner (used by auto-refresh)
  // ✅ `isFilterChange` = true means show filterLoading (dropdown spinner)
  const fetchData = async (
    filterKey = filter,
    silent = false,
    isFilterChange = false
  ) => {
    try {
      if (!silent && !isFilterChange) setLoading(true);
      if (isFilterChange) setFilterLoading(true);
      setError(null);

      const res = await EarningsOverview({ filter: filterKey });

      if (res.success) {
        setApiData(res.data);
        setLastUpdated(new Date());
      } else {
        setError(res?.message || "API returned unsuccessful response");
      }
    } catch (err) {
      console.error("Earnings fetch error:", err);
      if (!silent) {
        setError(
          err?.response?.data?.message || "Failed to load earnings data"
        );
      }
    } finally {
      if (!silent && !isFilterChange) setLoading(false);
      if (isFilterChange) setFilterLoading(false);
    }
  };

  // Filter change → auto fetch (show filterLoading, keep old data visible)
  useEffect(() => {
    fetchData(filter, false, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  // ✅ AUTO REFRESH — every 30 seconds (silent, no spinner)
  useEffect(() => {
    const interval = setInterval(() => {
      fetchData(filterRef.current, true); // silent refresh
    }, AUTO_REFRESH_INTERVAL);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ REFRESH ON TAB FOCUS — jab user wapas aaye tab fresh data
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        fetchData(filterRef.current, true); // silent
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============ EXTRACTED VALUES ============
  const combined = apiData?.combinedSummary || {
    totalProfit: 0,
    totalLoss: 0,
    netResult: 0,
  };
  const gameBreakdown = apiData?.gameWiseBreakdown || [];

  // ============ ANIMATED COUNT-UP ============
  useEffect(() => {
    const end = {
      totalProfit: combined.totalProfit || 0,
      totalLoss: combined.totalLoss || 0,
      netResult: combined.netResult || 0,
    };

    const duration = 600;
    const steps = 25;
    const interval = duration / steps;
    let step = 0;
    const start = { ...animatedTotals };

    const timer = setInterval(() => {
      step++;
      const progress = step / steps;
      const ease = 1 - Math.pow(1 - progress, 3);
      setAnimatedTotals({
        totalProfit:
          start.totalProfit + (end.totalProfit - start.totalProfit) * ease,
        totalLoss: start.totalLoss + (end.totalLoss - start.totalLoss) * ease,
        netResult: start.netResult + (end.netResult - start.netResult) * ease,
      });
      if (step >= steps) {
        clearInterval(timer);
        setAnimatedTotals(end);
      }
    }, interval);

    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [combined.totalProfit, combined.totalLoss, combined.netResult]);

  // ============ HANDLERS ============
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData(filter, false);
    setTimeout(() => setRefreshing(false), 400);
  };

  const activeFilter =
    FILTER_OPTIONS.find((f) => f.key === filter) || FILTER_OPTIONS[0];

  // ============ LOADING ============
  if (loading && !apiData) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "60vh" }}
      >
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status"></div>
          <p className="text-muted small mb-0">Loading earnings...</p>
        </div>
      </div>
    );
  }

  // ============ ERROR ============
  if (error && !apiData) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "60vh" }}
      >
        <div className="text-center">
          <FiAlertCircle size={40} className="text-danger mb-2" />
          <h6 className="fw-bold mb-1">Failed to Load</h6>
          <p className="text-muted small mb-3">{error}</p>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => fetchData(filter)}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* ===================== HEADER ===================== */}
      <div className="d-flex align-items-center flex-wrap gap-2 mb-3">
        <div className="d-flex align-items-center">
          <div className="me-2 icon-box-md d-flex align-items-center justify-content-center">
            <FiTrendingUp className="text-white" size={20} />
          </div>
          <div>
            <h5
              className="mb-0 fw-bold"
              style={{ color: "#0F172A", fontSize: "1.15rem" }}
            >
              Admin Earning
            </h5>
            <small style={{ color: "#94A3B8", fontSize: "0.75rem" }}>
              Real-time profit & loss overview
            </small>
          </div>
        </div>

        <div className="ms-auto d-flex flex-wrap gap-2 align-items-center">
          {/* ✅ Live indicator */}
          <span
            className="d-flex align-items-center gap-1 px-2 py-1 rounded-3"
            style={{
              background: "#F0FDF4",
              color: "#15803D",
              fontSize: "0.7rem",
              fontWeight: 600,
              border: "1px solid #BBF7D0",
            }}
            title={
              lastUpdated
                ? `Last updated: ${formatTime(lastUpdated)}`
                : "Live"
            }
          >
            <span
              className="live-dot"
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: "#16A34A",
                display: "inline-block",
              }}
            />
            Live
            {lastUpdated && (
              <span style={{ opacity: 0.7, fontWeight: 500 }}>
                · {formatTime(lastUpdated)}
              </span>
            )}
          </span>

          {/* ✅ Filter */}
          <div className="position-relative">
            <button
              className="btn btn-light btn-sm d-flex align-items-center gap-2 px-2 py-1 rounded-3 border filter-btn"
              onClick={() => !filterLoading && setFilterOpen(!filterOpen)}
              disabled={filterLoading}
              style={{
                fontWeight: 600,
                color: "#475569",
                fontSize: "0.8rem",
                opacity: filterLoading ? 0.85 : 1,
                cursor: filterLoading ? "wait" : "pointer",
              }}
            >
              {filterLoading ? (
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                  style={{ width: 13, height: 13, borderWidth: 2 }}
                ></span>
              ) : (
                <FiCalendar size={13} />
              )}
              {filterLoading ? "Loading..." : activeFilter.label}
              {!filterLoading && (
                <FiChevronDown
                  size={13}
                  style={{
                    transform: filterOpen ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 0.2s ease",
                  }}
                />
              )}
            </button>

            {filterOpen && (
              <>
                <div
                  className="position-fixed top-0 start-0 w-100 h-100"
                  style={{ zIndex: 999 }}
                  onClick={() => !filterLoading && setFilterOpen(false)}
                ></div>
                <div
                  className="position-absolute end-0 mt-2 bg-white rounded-3 shadow-lg filter-dropdown"
                  style={{
                    minWidth: 150,
                    zIndex: 1000,
                    border: "1px solid #E2E8F0",
                    overflow: "hidden",
                  }}
                >
                  {FILTER_OPTIONS.map((opt) => {
                    const isActive = filter === opt.key;
                    const isCurrentlyLoading = filterLoading && isActive;

                    return (
                      <button
                        key={opt.key}
                        className="w-100 text-start px-3 py-2 border-0 filter-option d-flex align-items-center justify-content-between"
                        style={{
                          color: isActive ? "#4F46E5" : "#475569",
                          fontWeight: isActive ? 600 : 500,
                          fontSize: "0.8rem",
                          background: isActive ? "#EEF2FF" : "transparent",
                          cursor: filterLoading ? "wait" : "pointer",
                          opacity: filterLoading && !isActive ? 0.5 : 1,
                        }}
                        disabled={filterLoading}
                        onClick={() => {
                          if (!filterLoading) {
                            setFilter(opt.key);
                            setFilterOpen(false);
                          }
                        }}
                      >
                        <span>{opt.label}</span>
                        {isCurrentlyLoading && (
                          <span
                            className="spinner-border spinner-border-sm text-primary"
                            role="status"
                            aria-hidden="true"
                            style={{ width: 12, height: 12, borderWidth: 2 }}
                          ></span>
                        )}
                      </button>
                    );
                  })}

                  {/* ✅ Fetching indicator inside dropdown */}
                  {filterLoading && (
                    <div
                      className="px-3 py-2 text-center"
                      style={{
                        fontSize: "0.7rem",
                        color: "#64748B",
                        borderTop: "1px solid #E2E8F0",
                        background: "#F8FAFC",
                      }}
                    >
                      <span
                        className="spinner-border spinner-border-sm me-1"
                        role="status"
                        style={{ width: 10, height: 10, borderWidth: 2 }}
                      ></span>
                      Fetching data...
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          <button
            className="btn btn-light btn-sm d-flex align-items-center gap-2 px-2 py-1 rounded-3 border"
            onClick={handleRefresh}
            disabled={refreshing || filterLoading}
            style={{
              fontWeight: 600,
              color: "#475569",
              fontSize: "0.8rem",
              opacity: refreshing || filterLoading ? 0.7 : 1,
            }}
          >
            <FiRefreshCw
              size={13}
              className={refreshing || filterLoading ? "spin" : ""}
            />
            {refreshing ? "..." : "Refresh"}
          </button>
        </div>
      </div>

      {/* ✅ TOP PROGRESS BAR — jab filter change ho raha ho */}
      {filterLoading && (
        <div
          className="progress mb-3"
          style={{ height: 3, borderRadius: 4, background: "#EEF2FF" }}
        >
          <div
            className="progress-bar progress-bar-striped progress-bar-animated"
            role="progressbar"
            style={{
              width: "100%",
              background: "linear-gradient(90deg, #4F46E5, #7C3AED)",
            }}
          ></div>
        </div>
      )}

      {/* ===================== COMBINED SUMMARY ===================== */}
      <div
        className="mb-3"
        style={{
          opacity: filterLoading ? 0.55 : 1,
          transition: "opacity 0.25s ease",
          pointerEvents: filterLoading ? "none" : "auto",
        }}
      >
        <div
          className="card border-0 smooth-card fade-in"
          style={{ borderRadius: 14 }}
        >
          <div className="card-body p-3">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
              <div className="d-flex align-items-center gap-2">
                <div
                  className="d-flex align-items-center justify-content-center rounded-3"
                  style={{
                    width: 28,
                    height: 28,
                    background: "#EEF2FF",
                    color: "#4F46E5",
                  }}
                >
                  <FiActivity size={14} />
                </div>
                <div>
                  <h6
                    className="fw-bold mb-0"
                    style={{ color: "#0F172A", fontSize: "0.9rem" }}
                  >
                    Combined Summary
                  </h6>
                  <small style={{ color: "#94A3B8", fontSize: "0.7rem" }}>
                    {activeFilter.label} • Both games
                  </small>
                </div>
              </div>
              <span
                className="badge rounded-pill"
                style={{
                  background: "#EEF2FF",
                  color: "#4F46E5",
                  fontWeight: 600,
                  fontSize: "0.65rem",
                  padding: "4px 10px",
                }}
              >
                {activeFilter.short}
              </span>
            </div>

            <div className="row g-2">
              {/* Total Profit */}
              <div className="col-md-4 col-12">
                <div
                  className="p-3 rounded-3 h-100 summary-box"
                  style={{
                    background:
                      "linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 100%)",
                    border: "1px solid #BBF7D0",
                  }}
                >
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <small
                      className="fw-semibold text-uppercase"
                      style={{
                        fontSize: "0.62rem",
                        letterSpacing: "0.05em",
                        color: "#15803D",
                      }}
                    >
                      Total Profit
                    </small>
                    <div
                      className="d-flex align-items-center justify-content-center rounded-circle"
                      style={{
                        width: 24,
                        height: 24,
                        background: "#16A34A",
                        color: "#fff",
                      }}
                    >
                      <FiArrowUpRight size={12} style={{ strokeWidth: 3 }} />
                    </div>
                  </div>
                  <h5
                    className="fw-bold mb-0 counter"
                    style={{
                      color: "#15803D",
                      fontSize: "1.15rem",
                      letterSpacing: "-0.02em",
                    }}
                  >
                    {formatCurrency(animatedTotals.totalProfit)}
                  </h5>
                </div>
              </div>

              {/* Total Loss */}
              <div className="col-md-4 col-12">
                <div
                  className="p-3 rounded-3 h-100 summary-box"
                  style={{
                    background:
                      "linear-gradient(135deg, #FEF2F2 0%, #FEE2E2 100%)",
                    border: "1px solid #FECACA",
                  }}
                >
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <small
                      className="fw-semibold text-uppercase"
                      style={{
                        fontSize: "0.62rem",
                        letterSpacing: "0.05em",
                        color: "#B91C1C",
                      }}
                    >
                      Total Loss
                    </small>
                    <div
                      className="d-flex align-items-center justify-content-center rounded-circle"
                      style={{
                        width: 24,
                        height: 24,
                        background: "#DC2626",
                        color: "#fff",
                      }}
                    >
                      <FiArrowDownRight size={12} style={{ strokeWidth: 3 }} />
                    </div>
                  </div>
                  <h5
                    className="fw-bold mb-0 counter"
                    style={{
                      color: "#B91C1C",
                      fontSize: "1.15rem",
                      letterSpacing: "-0.02em",
                    }}
                  >
                    {formatCurrency(animatedTotals.totalLoss)}
                  </h5>
                </div>
              </div>

              {/* Net Result */}
              <div className="col-md-4 col-12">
                <div
                  className="p-3 rounded-3 h-100 text-white position-relative overflow-hidden summary-box"
                  style={{
                    background:
                      animatedTotals.netResult >= 0
                        ? "linear-gradient(135deg, #16A34A 0%, #15803D 100%)"
                        : "linear-gradient(135deg, #DC2626 0%, #991B1B 100%)",
                    boxShadow:
                      animatedTotals.netResult >= 0
                        ? "0 6px 16px rgba(22, 163, 74, 0.25)"
                        : "0 6px 16px rgba(220, 38, 38, 0.25)",
                  }}
                >
                  <div
                    className="position-absolute"
                    style={{
                      width: 80,
                      height: 80,
                      borderRadius: "50%",
                      background: "rgba(255,255,255,0.08)",
                      top: -30,
                      right: -30,
                    }}
                  ></div>
                  <div className="d-flex justify-content-between align-items-start mb-2 position-relative">
                    <small
                      className="fw-semibold text-uppercase"
                      style={{
                        fontSize: "0.62rem",
                        letterSpacing: "0.05em",
                        opacity: 0.9,
                      }}
                    >
                      Net Result
                    </small>
                    <div
                      className="d-flex align-items-center justify-content-center rounded-circle"
                      style={{
                        width: 24,
                        height: 24,
                        background: "rgba(255,255,255,0.2)",
                      }}
                    >
                      <FiActivity size={12} />
                    </div>
                  </div>
                  <h5
                    className="fw-bold mb-0 position-relative counter"
                    style={{ fontSize: "1.15rem", letterSpacing: "-0.02em" }}
                  >
                    {animatedTotals.netResult >= 0 ? "+" : "-"}
                    {formatCurrency(animatedTotals.netResult)}
                  </h5>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ===================== SECTION TITLE ===================== */}
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
        <div className="d-flex align-items-center gap-2">
          <h6
            className="fw-bold mb-0"
            style={{ color: "#0F172A", fontSize: "0.9rem" }}
          >
            Game-wise Breakdown
          </h6>
          <span
            className="badge rounded-pill"
            style={{
              background: "#EEF2FF",
              color: "#4F46E5",
              fontWeight: 600,
              fontSize: "0.65rem",
            }}
          >
            {gameBreakdown.length} Games
          </span>
        </div>
        <small style={{ color: "#94A3B8", fontSize: "0.75rem" }}>
          {activeFilter.label}
        </small>
      </div>

      {/* ===================== GAME CARDS ===================== */}
      <div
        className="row g-3"
        style={{
          opacity: filterLoading ? 0.55 : 1,
          transition: "opacity 0.25s ease",
          pointerEvents: filterLoading ? "none" : "auto",
        }}
      >
        {gameBreakdown.map((game, idx) => {
          const config = GAME_CONFIG[game.game] || {
            name: game.game,
            icon: <FiActivity />,
            accentColor: "#64748B",
            accentBg: "#F1F5F9",
          };
          const net = game.netResult || 0;
          const isProfit = net >= 0;

          return (
            <div
              className="col-xl-6 col-lg-6 col-md-12 col-12 fade-in"
              key={game.game}
              style={{ animationDelay: `${idx * 80}ms` }}
            >
              <div
                className="card border-0 h-100 earning-card smooth-card"
                style={{ borderRadius: 14, cursor: "default" }}
              >
                <div className="card-body p-3">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div className="d-flex align-items-center gap-2">
                      <div
                        className="game-icon-box d-flex align-items-center justify-content-center"
                        style={{
                          background: config.accentBg,
                          color: config.accentColor,
                        }}
                      >
                        {config.icon}
                      </div>
                      <div>
                        <h6
                          className="fw-bold mb-0"
                          style={{ color: "#0F172A", fontSize: "0.9rem" }}
                        >
                          {config.name}
                        </h6>
                        <small
                          style={{ color: "#94A3B8", fontSize: "0.7rem" }}
                        >
                          {game.totalRounds} rounds
                        </small>
                      </div>
                    </div>
                  </div>

                  <div className="mb-2">
                    <p
                      className="text-uppercase fw-semibold mb-1"
                      style={{
                        fontSize: "0.62rem",
                        letterSpacing: "0.05em",
                        color: "#94A3B8",
                      }}
                    >
                      Total Profit / Loss
                    </p>
                    <h4
                      className="fw-bold mb-0 d-flex align-items-center gap-1 counter"
                      style={{
                        color: isProfit ? "#16A34A" : "#DC2626",
                        fontSize: "1.4rem",
                        letterSpacing: "-0.02em",
                      }}
                    >
                      {isProfit ? (
                        <FiArrowUpRight size={22} style={{ strokeWidth: 3 }} />
                      ) : (
                        <FiArrowDownRight
                          size={22}
                          style={{ strokeWidth: 3 }}
                        />
                      )}
                      {isProfit ? "+" : "-"}
                      {formatCurrency(net)}
                    </h4>
                  </div>

                  <div
                    className="d-flex gap-2 pt-2 mt-1"
                    style={{ borderTop: "1px dashed #E2E8F0" }}
                  >
                    <div className="flex-fill">
                      <div className="d-flex align-items-center gap-1 mb-1">
                        <FiArrowUpRight
                          size={11}
                          style={{ color: "#16A34A", strokeWidth: 3 }}
                        />
                        <small
                          className="fw-semibold"
                          style={{ color: "#64748B", fontSize: "0.62rem" }}
                        >
                          PROFIT
                        </small>
                      </div>
                      <span
                        className="fw-bold"
                        style={{ color: "#16A34A", fontSize: "0.8rem" }}
                      >
                        {formatCurrency(game.profit)}
                      </span>
                    </div>
                    <div style={{ width: 1, background: "#E2E8F0" }}></div>
                    <div className="flex-fill">
                      <div className="d-flex align-items-center gap-1 mb-1">
                        <FiArrowDownRight
                          size={11}
                          style={{ color: "#DC2626", strokeWidth: 3 }}
                        />
                        <small
                          className="fw-semibold"
                          style={{ color: "#64748B", fontSize: "0.62rem" }}
                        >
                          LOSS
                        </small>
                      </div>
                      <span
                        className="fw-bold"
                        style={{ color: "#DC2626", fontSize: "0.8rem" }}
                      >
                        {formatCurrency(game.loss)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {gameBreakdown.length === 0 && (
          <div className="col-12">
            <div className="text-center py-5 bg-white rounded-3">
              <FiActivity size={32} className="text-muted mb-2" />
              <p className="text-muted small mb-0">No game data available</p>
            </div>
          </div>
        )}
      </div>

      {/* ===================== STYLES ===================== */}
      <style>{`
        .icon-box-md {
          width: 40px;
          height: 40px;
          border-radius: 12px;
          background: linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%);
          box-shadow: 0 6px 14px rgba(79, 70, 229, 0.28);
        }
        .game-icon-box {
          width: 36px;
          height: 36px;
          border-radius: 10px;
          font-size: 1rem;
        }
        .smooth-card {
          box-shadow: 0 3px 14px rgba(15, 23, 42, 0.05);
          transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .summary-box { transition: all 0.3s ease; }
        .summary-box:hover { transform: translateY(-2px); }
        .earning-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 24px rgba(15, 23, 42, 0.1) !important;
        }
        .filter-btn { transition: all 0.2s ease; }
        .filter-btn:hover {
          background: #F1F5F9 !important;
          border-color: #CBD5E1 !important;
        }
        .filter-dropdown { animation: dropdownFade 0.2s ease; }
        @keyframes dropdownFade {
          from { opacity: 0; transform: translateY(-6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .filter-option { transition: all 0.15s ease; cursor: pointer; }
        .filter-option:hover { background: #F8FAFC !important; }
        .fade-in { animation: fadeIn 0.45s ease both; }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .counter { transition: color 0.3s ease; }

        /* ✅ Pulsing green dot for "Live" indicator */
        .live-dot {
          animation: livePulse 1.5s ease-in-out infinite;
        }
        @keyframes livePulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(1.3); }
        }
      `}</style>
    </>
  );
};

export default AdminEarning;